import { Alert, AlertTitle, Box, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import useAPI from "../../../../../api/hooks/useAPI";
import useColors from "../../../../../api/hooks/useColors";
import { controlKeys } from "../../../../../api/queryKeys";
import CustomToolTip from "../../../../../components/CustomTooltip";
import StyledDataGrid from "../../../../../components/StyledDataGrid";
import SvgToIcon from "../../../../../icons/SvgToIcon";
import ChipColor from "../../../../remediation/plan/components/ChipColor";
import { IntegrationsMapping } from "../../../integrations/IntegrationsMapping";
import { detectionIcons } from "../../icons/iconExport";

const GapsTabRisk = ({ entityData, handleCellClick }) => {
  const colors = useColors();
  const { fetchControlsBatch, trackEvent } = useAPI();

  const controlsQuery = useQuery({
    queryKey: controlKeys.batchControls(entityData?.related_control_ids || []),
    queryFn: () => fetchControlsBatch(entityData?.related_control_ids || []),
    select: (response) => response.data,
  });

  const gapColumns = useMemo(
    () => [
      {
        field: "control_id",
        headerName: "ID",
        headerAlign: "center",
        width: 70,
        resizable: false,
        renderCell: (params) => (
          <Box width="100%" display="flex" justifyContent="center">
            <Typography variant="caption" color={colors.text_primary}>
              #{params.value}
            </Typography>
          </Box>
        ),
        valueGetter: (params) => params.value,
      },
      {
        field: "name_for_gap_state",
        headerName: "Description",
        flex: 1,
        renderCell: (params) => (
          <Box display="flex" flexDirection="row" gap={1} alignItems="center">
            {params.row.is_detected && (
              <Box display="flex" alignItems="center">
                <SvgToIcon IconLogo={detectionIcons.DetectionRed} />
              </Box>
            )}
            <CustomToolTip title={params.value} placement="top">
              <Typography variant="caption" color={colors.text_primary}>
                {params.value}
              </Typography>
            </CustomToolTip>
          </Box>
        ),
      },
      {
        field: "tool",
        headerName: "Tool",
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <SvgToIcon
            IconLogo={IntegrationsMapping[params.value?.toLowerCase()]?.logo}
            width="40px"
          />
        ),
      },
      {
        field: "severity",
        headerName: "Severity",
        minWidth: 110,
        renderCell: (params) => (
          <ChipColor
            value={params.value}
            type="controlPriority"
            disabled={!params.row.is_gap}
          />
        ),
      },
    ],
    [colors.text_primary]
  );

  const gapRows = useMemo(
    () =>
      controlsQuery.data?.map((row) => ({
        id: row.control_id,
        ...row,
      })) || [],
    [controlsQuery.data]
  );

  const handleCellClickWithTracking = (params) => {
    handleCellClick(params);
    trackEvent(
      `Open Gap Details for control ${params.row.control_id} in new tab`
    );
  };

  if (!entityData?.related_control_ids?.length) {
    return (
      <Alert severity="success" sx={{ m: 2 }}>
        <AlertTitle>You're all set!</AlertTitle>
        No gaps affect this entity.
      </Alert>
    );
  }

  return (
    <StyledDataGrid
      sx={{ cursor: "pointer" }}
      columns={gapColumns}
      rows={gapRows}
      onCellClick={handleCellClickWithTracking}
      loading={controlsQuery.isLoading}
      pageSizeOptionsOverride={[3]}
      autoHeight
      boxSXProps={{
        border: "none",
        width: "100%",
        ".MuiDataGrid-root": {
          border: "none",
          boxShadow: "none",
        },
        ".MuiDataGrid-main": {},
      }}
      initialState={{
        sorting: {
          sortModel: [{ field: "severity", sort: "desc" }],
        },
      }}
    />
  );
};

export default GapsTabRisk;
